import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/api/client'
import { notify } from '@/design-system'

/**
 * Caching policy:
 * - Lists are fresh for 30s (operations data changes constantly, but tab switches shouldn't refetch).
 * - Identical requests in flight are deduplicated by query key.
 * - Auth / permission / validation errors are never retried; network and 5xx are retried with backoff.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
      retry: (count, error) => (error instanceof ApiError ? error.isRetryable && count < 2 : count < 1),
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: { retry: false, networkMode: 'online' },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Background refetch failures on already-rendered data surface as a toast; first-load errors render inline.
      if (query.meta?.silent || query.state.data === undefined) return
      if (error instanceof ApiError && error.status === 401) return
      notify.error(error)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _v, _c, mutation) => {
      if (mutation.meta?.silent) return
      if (mutation.options.onError) return
      if (error instanceof ApiError && error.status === 401) return
      notify.error(error)
    },
  }),
})
